import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { ColumnDef, SortingState } from "@tanstack/react-table";
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { AdminHeader } from "@trillion/ui/components/admin/admin-header";
import { PageHeader } from "@trillion/ui/components/admin/page-header";
import { DataTableWrapper } from "@trillion/ui/components/admin/data-table-wrapper";
import { ConfirmDialog } from "@trillion/ui/components/admin/confirm-dialog";
import { SortableTableHead } from "@trillion/ui/components/admin/sortable-header";
import { SORTABLE_GRID_OPTIONS, orderByKey } from "@trillion/ui/lib/data-grid-sort";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@trillion/ui/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@trillion/ui/components/ui/table";
import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { jobsApi } from "@/lib/admin-api";
import { DEFAULT_RUN_TAB, resolveRunTab } from "@/lib/run-tabs";
import {
  IN_FLIGHT_WINDOW_NOTE,
  readRunDays,
  runWindowDescription,
  withRunDays,
  type RunDays,
} from "@/lib/run-days";
import {
  getEnqueuedColumns,
  getFailedColumns,
  getProcessingColumns,
  getScheduledColumns,
  getSucceededColumns,
  jobLabel,
} from "@/components/runs/run-columns";
import { ErrorInspector } from "@/components/runs/error-inspector";
import { RunLogPanel } from "@/components/runs/run-log-panel";
import { RunDaysSelector } from "@/components/runs/run-days-selector";
import { GridFilterInput, NO_MATCHES_TEXT } from "@/components/grid-filter-input";
import { useRunList } from "@/hooks/use-run-list";
import type { FailedJob, JobDescriptor, RunHistoryEnvelope, SucceededJob } from "@/lib/jobs-types";
import { filterByWildcard } from "@/lib/wildcard-match";

export { POLL_INTERVAL_MS, STALE_REQUEST_MS } from "@/hooks/use-run-list";

// One named default sort per tab. Normalizing the base order to `job_id` (below) would otherwise
// replace each tab's recency presentation with id order, so the recency view is PINNED rather than
// dropped — the same "named constant per page" rule the recurring and settings grids use.
export const SUCCEEDED_DEFAULT_SORT: SortingState = [{ id: "succeeded_at", desc: true }];
export const FAILED_DEFAULT_SORT: SortingState = [{ id: "failed_at", desc: true }];
export const PROCESSING_DEFAULT_SORT: SortingState = [{ id: "started_at", desc: true }];
export const ENQUEUED_DEFAULT_SORT: SortingState = [{ id: "enqueued_at", desc: true }];
export const SCHEDULED_DEFAULT_SORT: SortingState = [{ id: "enqueue_at", desc: false }];

/** What every run row carries: its id (the grid's domain key) and the job it ran (the Job column). */
interface RunRow {
  job_id: string;
  job: JobDescriptor | null;
}

/**
 * The Run History page's wildcard filter pattern (sc-114202). `RunsPage` is its only provider, so one pattern
 * serves every tab; the job details page embeds `SucceededRunsTab` / `FailedRunsTab` without a provider and
 * gets the unfiltered default `""`. Module-private: nothing outside this file can read or set it.
 */
const RunFilterContext = createContext("");

// Presentational table shared by every run tab.
function RunTable<T extends RunRow>({
  data,
  columns,
  isLoading,
  title,
  description,
  empty,
  defaultSorting,
}: {
  data: T[];
  columns: ColumnDef<T>[];
  isLoading: boolean;
  title: string;
  description?: string;
  empty: string;
  defaultSorting: SortingState;
}) {
  const [sorting, setSorting] = useState<SortingState>(defaultSorting);
  const filter = useContext(RunFilterContext);

  // The filter tests each row against the Job column's own text (`Type.Method`); the tabs' polling keeps
  // reading the fetched `data`, so a hidden in-flight row still keeps its grid polling.
  const filtered = useMemo(
    () => filterByWildcard(data, filter, (row) => [jobLabel(row.job)]),
    [data, filter]
  );

  // Normalized base order, so the tie-break for equal or missing values IS the run id and a poll
  // cannot reshuffle rows under the cursor.
  const rows = useMemo(() => orderByKey(filtered, (row) => row.job_id), [filtered]);

  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    ...SORTABLE_GRID_OPTIONS,
    getRowId: (row) => row.job_id,
    state: { sorting },
    onSortingChange: setSorting,
  });

  return (
    <DataTableWrapper title={title} description={description} isLoading={isLoading}>
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <SortableTableHead key={header.id} header={header} />
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.length ? (
            table.getRowModel().rows.map((row) => (
              <TableRow key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell
                colSpan={columns.length}
                className="h-24 text-center text-muted-foreground"
              >
                {data.length > 0 ? NO_MATCHES_TEXT : empty}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </DataTableWrapper>
  );
}

// A non-mutating run-history tab (succeeded): one `{ rows, truncated }` window per mount. Radix
// unmounts inactive tab content, so this fetches once each time its tab is opened.
function SimpleRunTab<T extends RunRow>({
  fetcher,
  days,
  columns,
  title,
  empty,
  defaultSorting,
  pollWhileInFlight = false,
}: {
  fetcher: (signal?: AbortSignal) => Promise<RunHistoryEnvelope<T>>;
  days: RunDays;
  columns: ColumnDef<T>[];
  title: string;
  empty: string;
  defaultSorting: SortingState;
  /** In-flight tabs poll while active; Succeeded and Failed stay fetch-on-open. */
  pollWhileInFlight?: boolean;
}) {
  const { value, isLoading } = useRunList(
    fetcher,
    { rows: [], truncated: false },
    (page) => pollWhileInFlight && page.rows.length > 0
  );
  const data = value.rows;
  const truncated = value.truncated;

  return (
    <RunTable
      data={data}
      columns={columns}
      isLoading={isLoading}
      title={title}
      description={runWindowDescription(days, truncated)}
      empty={empty}
      defaultSorting={defaultSorting}
    />
  );
}

// A run tab whose rows carry a Delete action behind a destructive confirm (processing / scheduled /
// enqueued). Load-on-mount like SimpleRunTab, plus FailedRunsTab's delete-confirm state. Radix remounts
// the tab on re-activation, so each open re-fetches. `withRunLog` adds a View log action that opens the
// live tail; the tailed job id lives in its own state, not in `data`, so the panel outlives the row.
function DeletableRunTab<T extends RunRow>({
  fetcher,
  getColumns,
  title,
  empty,
  confirmTitle,
  confirmDescription,
  confirmLabel,
  defaultSorting,
  pollWhileInFlight = false,
  withRunLog = false,
  description,
}: {
  fetcher: (signal?: AbortSignal) => Promise<T[]>;
  getColumns: (actions: {
    onDelete: (job: T) => void;
    onView?: (job: T) => void;
  }) => ColumnDef<T>[];
  title: string;
  empty: string;
  confirmTitle: string;
  confirmDescription: (jobId: string) => string;
  confirmLabel: string;
  defaultSorting: SortingState;
  /** In-flight tabs poll while active; Succeeded and Failed stay fetch-on-open. */
  pollWhileInFlight?: boolean;
  /** Adds a View log row action that opens the run's live log tail (Processing only). */
  withRunLog?: boolean;
  /** The grid's description line (the in-flight tabs say the days window does not apply). */
  description?: string;
}) {
  const { value: data, isLoading, load } = useRunList<T[]>(
    fetcher,
    [],
    (rows) => pollWhileInFlight && rows.length > 0
  );
  const [deleteJob, setDeleteJob] = useState<T | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  // The tailed run, held apart from `data`: the grid drops a job the moment it leaves Processing, and the
  // panel must keep tailing it until the tail itself reports `finished`.
  const [logJobId, setLogJobId] = useState<string | null>(null);
  const openLog = useCallback((job: T) => setLogJobId(job.job_id), []);

  const confirmDelete = useCallback(async () => {
    if (!deleteJob) return;
    setIsDeleting(true);
    try {
      await jobsApi.remove(deleteJob.job_id);
      toast.success("Job deleted");
      setDeleteJob(null);
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to delete job");
    } finally {
      setIsDeleting(false);
    }
  }, [deleteJob, load]);

  const columns = useMemo(
    () =>
      getColumns(
        withRunLog ? { onDelete: setDeleteJob, onView: openLog } : { onDelete: setDeleteJob }
      ),
    [getColumns, withRunLog, openLog]
  );

  return (
    <>
      <RunTable
        data={data}
        columns={columns}
        isLoading={isLoading}
        title={title}
        description={description}
        empty={empty}
        defaultSorting={defaultSorting}
      />
      <ConfirmDialog
        open={deleteJob !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteJob(null);
        }}
        title={confirmTitle}
        description={confirmDescription(deleteJob?.job_id ?? "")}
        confirmLabel={confirmLabel}
        onConfirm={confirmDelete}
        variant="destructive"
        isLoading={isDeleting}
      />
      {withRunLog && (
        <RunLogPanel
          jobId={logJobId}
          onOpenChange={(open) => {
            if (!open) setLogJobId(null);
          }}
        />
      )}
    </>
  );
}

// Succeeded runs: the fetch-on-open SimpleRunTab plus a Replay log row action (sc-114198). The replayed
// job id lives in its own state, so opening a replay starts no grid polling. `recurringJobId` narrows it to one
// recurring job's runs (the job details page, sc-114201).
export function SucceededRunsTab({ days, recurringJobId }: { days: RunDays; recurringJobId?: string }) {
  const [replayJobId, setReplayJobId] = useState<string | null>(null);
  const columns = useMemo(
    () => getSucceededColumns({ onReplay: (job: SucceededJob) => setReplayJobId(job.job_id) }),
    []
  );

  return (
    <>
      <SimpleRunTab
        fetcher={(signal) =>
          recurringJobId === undefined
            ? jobsApi.succeeded(days, signal)
            : jobsApi.succeededFor(recurringJobId, days, signal)
        }
        days={days}
        columns={columns}
        title="Succeeded"
        empty="No succeeded runs."
        defaultSorting={SUCCEEDED_DEFAULT_SORT}
      />
      <RunLogPanel
        mode="replay"
        jobId={replayJobId}
        onOpenChange={(open) => {
          if (!open) setReplayJobId(null);
        }}
      />
    </>
  );
}

interface FailedRunsTabProps {
  days: RunDays;
  /** Narrows the grid to one recurring job's runs (the job details page, sc-114201). */
  recurringJobId?: string;
  /**
   * Called once with the job id after a successful Requeue and this grid's own reload — never for a failed or
   * refused (`requeued: false`) requeue. The details page uses it to follow the retry (a requeue reuses its job id, sc-114201).
   */
  onRequeued?: (jobId: string) => void;
}

// Failed runs carry row actions: Inspect (Sheet), Requeue, Replay log (sc-114198), Delete (confirm).
export function FailedRunsTab({ days, recurringJobId, onRequeued }: FailedRunsTabProps) {
  const { value, isLoading, load } = useRunList<RunHistoryEnvelope<FailedJob>>(
    (signal) =>
      recurringJobId === undefined
        ? jobsApi.failed(days, signal)
        : jobsApi.failedFor(recurringJobId, days, signal),
    { rows: [], truncated: false }
  );
  const data = value.rows;
  const truncated = value.truncated;
  const [inspectJob, setInspectJob] = useState<FailedJob | null>(null);
  const [replayJobId, setReplayJobId] = useState<string | null>(null);
  const [deleteJob, setDeleteJob] = useState<FailedJob | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Hangfire answers 200 with `requeued: false` when the row went stale (the job was deleted or expired since
  // this grid loaded). That is not a retry this action started, so it reloads the grid and follows nothing.
  const requeue = useCallback(
    async (job: FailedJob) => {
      try {
        const { requeued } = await jobsApi.requeue(job.job_id);
        if (!requeued) {
          toast.error(`Job ${job.job_id} could not be requeued — it no longer exists.`);
          await load();
          return;
        }
        toast.success("Job re-enqueued");
        await load();
        onRequeued?.(job.job_id);
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Failed to requeue job");
      }
    },
    [load, onRequeued]
  );

  const confirmDelete = useCallback(async () => {
    if (!deleteJob) return;
    setIsDeleting(true);
    try {
      await jobsApi.remove(deleteJob.job_id);
      toast.success("Job deleted");
      setDeleteJob(null);
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to delete job");
    } finally {
      setIsDeleting(false);
    }
  }, [deleteJob, load]);

  const columns = useMemo(
    () =>
      getFailedColumns({
        onInspect: setInspectJob,
        onRequeue: requeue,
        onDelete: setDeleteJob,
        onReplay: (job) => setReplayJobId(job.job_id),
      }),
    [requeue]
  );

  return (
    <>
      <RunTable
        data={data}
        columns={columns}
        isLoading={isLoading}
        title="Failed"
        description={runWindowDescription(days, truncated)}
        empty="No failed runs."
        defaultSorting={FAILED_DEFAULT_SORT}
      />
      <ErrorInspector
        job={inspectJob}
        onOpenChange={(open) => {
          if (!open) setInspectJob(null);
        }}
      />
      <RunLogPanel
        mode="replay"
        jobId={replayJobId}
        onOpenChange={(open) => {
          if (!open) setReplayJobId(null);
        }}
      />
      <ConfirmDialog
        open={deleteJob !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteJob(null);
        }}
        title="Delete job"
        description={`Delete failed job ${deleteJob?.job_id ?? ""}? This cannot be undone.`}
        confirmLabel="Delete"
        onConfirm={confirmDelete}
        variant="destructive"
        isLoading={isDeleting}
      />
    </>
  );
}

/** The routing boundary for `/runs/:tab?days=N` (sc-114199, sc-114200): the route param owns the selected
 *  tab and `days` owns the history window. A tab change rewrites only the pathname; a window change only
 *  `days`. Both `replace`, and so does the one redirect an unknown tab or an unsupported `days` takes. */
export function RunsPage() {
  const { tab } = useParams<{ tab?: string }>();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const activeTab = resolveRunTab(tab);
  const days = readRunDays(search);
  // The wildcard filter (sc-114202): page state, so it survives tab and window changes; never in the URL.
  const [filter, setFilter] = useState("");

  const selectTab = useCallback(
    (next: string) => {
      void navigate({ pathname: `/runs/${next}`, search }, { replace: true });
    },
    [navigate, search]
  );

  const selectDays = useCallback(
    (next: RunDays) => {
      void navigate({ pathname, search: withRunDays(search, next) }, { replace: true });
    },
    [navigate, pathname, search]
  );

  if (activeTab === null || days === null) {
    return (
      <Navigate
        to={{
          pathname: activeTab === null ? `/runs/${DEFAULT_RUN_TAB}` : pathname,
          search: days === null ? withRunDays(search, null) : search,
        }}
        replace
      />
    );
  }

  return (
    <div className="flex flex-col">
      <AdminHeader
        title="Run History"
        breadcrumbs={[{ label: "Dashboard", href: "/" }, { label: "Run History" }]}
      />
      <div className="flex flex-col gap-6 p-6">
        <PageHeader
          title="Run History"
          description="Hangfire job runs by state"
          actions={
            <>
              <GridFilterInput
                className="w-64"
                value={filter}
                onChange={setFilter}
                label="Filter runs by job"
                placeholder="Filter by job (* = any text)"
              />
              <RunDaysSelector value={days} onChange={selectDays} />
            </>
          }
        />

        <RunFilterContext.Provider value={filter}>
          <Tabs value={activeTab} onValueChange={selectTab} className="flex flex-col gap-4">
            <TabsList>
              <TabsTrigger value="succeeded">Succeeded</TabsTrigger>
              <TabsTrigger value="failed">Failed</TabsTrigger>
              <TabsTrigger value="processing">Processing</TabsTrigger>
              <TabsTrigger value="scheduled">Scheduled</TabsTrigger>
              <TabsTrigger value="enqueued">Enqueued</TabsTrigger>
            </TabsList>

            <TabsContent value="succeeded">
              <SucceededRunsTab key={days} days={days} />
            </TabsContent>
            <TabsContent value="failed">
              <FailedRunsTab key={days} days={days} />
            </TabsContent>
            <TabsContent value="processing">
              <DeletableRunTab
                fetcher={(signal) => jobsApi.processing(undefined, undefined, signal)}
                getColumns={getProcessingColumns}
                title="Processing"
                empty="No processing jobs."
                confirmTitle="Stop & delete job"
                confirmDescription={(id) =>
                  `Stop & delete running job ${id}? Hangfire cancels the run (a live worker kills the job's processes); the job will not be retried.`
                }
                confirmLabel="Stop & delete"
                defaultSorting={PROCESSING_DEFAULT_SORT}
                pollWhileInFlight
                withRunLog
                description={IN_FLIGHT_WINDOW_NOTE}
              />
            </TabsContent>
            <TabsContent value="scheduled">
              <DeletableRunTab
                fetcher={(signal) => jobsApi.scheduled(undefined, undefined, signal)}
                getColumns={getScheduledColumns}
                title="Scheduled"
                empty="No scheduled jobs."
                confirmTitle="Delete job"
                confirmDescription={(id) => `Delete job ${id}? It will not run. This cannot be undone.`}
                confirmLabel="Delete"
                defaultSorting={SCHEDULED_DEFAULT_SORT}
                pollWhileInFlight
                description={IN_FLIGHT_WINDOW_NOTE}
              />
            </TabsContent>
            <TabsContent value="enqueued">
              <DeletableRunTab
                fetcher={(signal) => jobsApi.enqueued(undefined, undefined, undefined, signal)}
                getColumns={getEnqueuedColumns}
                title="Enqueued"
                empty="No enqueued jobs."
                confirmTitle="Delete job"
                confirmDescription={(id) => `Delete job ${id}? It will not run. This cannot be undone.`}
                confirmLabel="Delete"
                defaultSorting={ENQUEUED_DEFAULT_SORT}
                pollWhileInFlight
                description={IN_FLIGHT_WINDOW_NOTE}
              />
            </TabsContent>
          </Tabs>
        </RunFilterContext.Provider>
      </div>
    </div>
  );
}
